import type { Assistant, AssistantStatus, CreateAssistantInput, VerifiedAssistant } from '@/application/assistants/assistantsTypes';

const API_BASE = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:3010/api';

interface ErrorResponse {
  error?: string;
}

async function errorMessage(response: Response): Promise<string> {
  try {
    const body = (await response.json()) as ErrorResponse;
    return body.error ?? 'assistants_request_failed';
  } catch {
    return 'assistants_request_failed';
  }
}

async function requestJson<T>(token: string, path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(init?.body ? { 'Content-Type': 'application/json' } : {}),
      ...init?.headers,
    },
  });

  if (!response.ok) {
    throw new Error(await errorMessage(response));
  }

  if (response.status === 204) {
    return undefined as T;
  }

  return (await response.json()) as T;
}

export function listAssistants(token: string): Promise<Assistant[]> {
  return requestJson<Assistant[]>(token, '/assistants');
}

export function createAssistant(token: string, input: CreateAssistantInput): Promise<Assistant> {
  return requestJson<Assistant>(token, '/assistants', { method: 'POST', body: JSON.stringify(input) });
}

/**
 * Verifica el email contra el ecosistema (solo lectura). Resuelve con los datos de la
 * persona si existe dentro del alcance del dueño; rechaza con `assistant_email_not_in_ecosystem`
 * (404) si no existe o queda fuera de alcance.
 */
export function verifyAssistantEmail(token: string, email: string): Promise<VerifiedAssistant> {
  return requestJson<VerifiedAssistant>(token, `/assistants/verify?email=${encodeURIComponent(email)}`);
}

export function renameAssistant(token: string, id: string, nombre: string): Promise<Assistant> {
  return requestJson<Assistant>(token, `/assistants/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    body: JSON.stringify({ nombre }),
  });
}

export function setAssistantStatus(token: string, id: string, status: AssistantStatus): Promise<Assistant> {
  return requestJson<Assistant>(token, `/assistants/${encodeURIComponent(id)}/status`, {
    method: 'PATCH',
    body: JSON.stringify({ status }),
  });
}

export function assignAssistantClinics(token: string, id: string, clinicaIds: string[]): Promise<Assistant> {
  return requestJson<Assistant>(token, `/assistants/${encodeURIComponent(id)}/clinics`, {
    method: 'PUT',
    body: JSON.stringify({ clinicaIds }),
  });
}

export function assignAssistantRole(token: string, id: string, panelRoleId: string | null): Promise<Assistant> {
  return requestJson<Assistant>(token, `/assistants/${encodeURIComponent(id)}/role`, {
    method: 'PATCH',
    body: JSON.stringify({ panelRoleId }),
  });
}

export function setAssistantPassword(token: string, id: string, password: string): Promise<Assistant> {
  return requestJson<Assistant>(token, `/assistants/${encodeURIComponent(id)}/password`, {
    method: 'PATCH',
    body: JSON.stringify({ password }),
  });
}

export function deleteAssistant(token: string, id: string): Promise<void> {
  return requestJson<void>(token, `/assistants/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export function recoverAssistantPassword(token: string, id: string): Promise<void> {
  return requestJson<void>(token, `/assistants/${encodeURIComponent(id)}/password/recover`, { method: 'POST' });
}

export function resetAssistantTwoFactor(token: string, id: string): Promise<void> {
  return requestJson<void>(token, `/assistants/${encodeURIComponent(id)}/two-factor/reset`, { method: 'POST' });
}
